/**
 * CoPilotDrawer — UX-04B: Application-wide deterministic workflow companion
 *
 * Entities supported:
 *  - Leads         : fetches /api/crm/leads/{id}/recommendations (use_ai=false)
 *  - Opportunities : fetches /api/crm/opportunities/{id}
 *  - Bookings      : fetches /api/a08/bookings/{id}
 *  - Events (CRM)  : fetches /api/events/{id}
 *  - All other pages: deterministic section guide
 *
 * Governance:
 *  - Zero LLM, zero AI, zero invented data
 *  - All record data fetched through authenticated backend APIs
 *  - No duplicate workflow engines
 *
 * Accessibility (UX-04B):
 *  - Escape key closes drawer
 *  - Focus restoration on close
 *  - Tab focus-trap for MOBILE overlay only (not desktop inline panel)
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import {
  X, Compass, Loader2, CheckCircle2, PhoneCall, Calendar,
  ClipboardList, Clock, Send, FileText, Zap, ArrowRight,
  ChevronDown, ChevronUp, Info, Target, Tag, CreditCard,
  CalendarDays, AlertCircle,
} from "lucide-react";
import { recommendationApi, crmApi, bookingApi, eventApi, responsibilityApi, crmMdmApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

// ─── Context resolver ─────────────────────────────────────────────────────────
function resolveCtx(pathname) {
  // ── Lead Detail (must precede generic /engage/leads) ──────────────────
  const leadDetailMatch = pathname.match(/^\/engage\/leads\/([^/]+)/);
  if (leadDetailMatch) {
    return { section: "CRM", page: "Lead Detail", isLeadDetail: true, leadId: leadDetailMatch[1] };
  }

  // ── Opportunity Detail ─────────────────────────────────────────────────
  const oppMatch = pathname.match(/^\/crm\/opportunities\/([^/]+)/);
  if (oppMatch) {
    return { section: "OPPORTUNITY", page: "Opportunity Detail", isOppDetail: true, oppId: oppMatch[1] };
  }

  // ── Booking Detail ─────────────────────────────────────────────────────
  const bookingMatch = pathname.match(/^\/crm\/bookings\/([^/]+)/);
  if (bookingMatch) {
    return { section: "BOOKING", page: "Booking Detail", isBookingDetail: true, bookingId: bookingMatch[1] };
  }

  // ── CRM Events (Master, Operations, Closeout, Financials) ─────────────
  const crmEventMatch = pathname.match(/^\/crm\/events\/([^/]+)/);
  if (crmEventMatch) {
    const subpage = pathname.includes("/operations") ? "Event Operations"
      : pathname.includes("/closeout") ? "Event Closeout"
      : pathname.includes("/financials") ? "Event Financials"
      : "Event Detail";
    return { section: "EXECUTION", page: subpage, isEventDetail: true, eventId: crmEventMatch[1] };
  }

  // ── Imagine event planning ─────────────────────────────────────────────
  const imagineEventMatch = pathname.match(/^\/imagine\/events\/([^/]+)/);
  if (imagineEventMatch) {
    return { section: "PLANNING", page: "Event Design", isImagineEvent: true };
  }

  // ── Generic section routes ─────────────────────────────────────────────
  if (/^\/engage\/leads/.test(pathname))       return { section: "CRM",            page: "Leads" };
  if (/^\/engage/.test(pathname))              return { section: "CRM",            page: "Enquiries" };
  if (/^\/crm\/inbox/.test(pathname))          return { section: "CRM",            page: "Communications" };
  if (/^\/crm\/opportunities/.test(pathname))  return { section: "OPPORTUNITY",    page: "Opportunities" };
  if (/^\/crm\/bookings/.test(pathname))       return { section: "BOOKING",        page: "Bookings" };
  if (/^\/crm/.test(pathname))                 return { section: "CRM",            page: "CRM" };
  if (/^\/ar/.test(pathname))                  return { section: "FINANCE",        page: "Accounts Receivable" };
  if (/^\/gl/.test(pathname))                  return { section: "FINANCE",        page: "General Ledger" };
  if (/^\/ap/.test(pathname))                  return { section: "FINANCE",        page: "Accounts Payable" };
  if (/^\/finance/.test(pathname))             return { section: "FINANCE",        page: "Finance" };
  if (/^\/imagine/.test(pathname))             return { section: "PLANNING",       page: "Event Planning" };
  if (/^\/planner/.test(pathname))             return { section: "PLANNING",       page: "Planning Cases" };
  if (/^\/events/.test(pathname))              return { section: "EXECUTION",      page: "Active Events" };
  if (/^\/business-setup/.test(pathname))      return { section: "SETTINGS",       page: "Business Setup" };
  if (/^\/administration/.test(pathname))      return { section: "ADMINISTRATION", page: "Administration" };
  if (/^\/enterprise/.test(pathname))          return { section: "ADMINISTRATION", page: "Enterprise" };
  if (/^\/marketing/.test(pathname))           return { section: "MARKETING",      page: "Analytics" };
  if (/^\/home/.test(pathname))                return { section: "HOME",           page: "Home" };
  if (/^\/dashboard/.test(pathname))           return { section: "HOME",           page: "Dashboard" };
  if (/^\/notifications/.test(pathname))       return { section: "HOME",           page: "Notifications" };
  if (/^\/guide/.test(pathname))               return { section: "HELP",           page: "User Guide" };
  if (/^\/uat/.test(pathname))                 return { section: "UAT",            page: "Control Center" };
  return { section: "PLATFORM", page: "" };
}

// ─── Deterministic section guides ─────────────────────────────────────────────
const SECTION_GUIDE = {
  HOME: {
    title: "Home",
    description: "Your personalised activity summary across all modules.",
    tips: [
      "Review assigned tasks and upcoming follow-ups from your dashboard",
      "Use the notification bell for real-time approval requests",
    ],
    pages: {},
  },
  CRM: {
    title: "Sales & CRM",
    description: "Leads, contacts, accounts and the full sales pipeline.",
    tips: [
      "All new leads require owner assignment before qualification can begin",
      "Log every call, meeting and note to maintain a complete audit trail",
      "Qualification requires confirmation of event scope, budget and target date",
    ],
    pages: {
      "Leads": [
        "Filter by status to identify leads needing immediate attention",
        "Use the NBA panel on Lead Detail for workflow-driven recommended actions",
      ],
      "Lead Detail": [
        "The Next Best Action panel surfaces recommended actions for this stage",
        "All status transitions are logged and visible in the Activity tab",
      ],
      "Communications": [
        "All inbound messages are threaded by lead to maintain context",
        "Mark threads resolved after completing the required follow-up action",
      ],
      "Enquiries": [
        "Web and email form enquiries arrive here for initial triaging",
        "Assign or convert an enquiry to create a qualified lead record",
      ],
    },
  },
  OPPORTUNITY: {
    title: "Opportunity Pipeline",
    description: "Track sales stage, event design, commercial terms and booking readiness.",
    tips: [
      "Stage progression: Prospecting → Needs Analysis → Proposal → Negotiation → Closed Won",
      "Event design and scope must be confirmed before generating commercial terms",
      "A Booking is created once the opportunity is Closed Won and client accepts terms",
    ],
    pages: {
      "Opportunities": [
        "Filter by stage to prioritise pipeline opportunities requiring action",
        "Stale opportunities (no activity in 14 days) need a follow-up touchpoint",
      ],
      "Opportunity Detail": [
        "Use the Scope tab to attach event requirements to this opportunity",
        "Commercial terms are generated from the accepted scope and service catalog",
        "The Booking tab shows the linked booking once terms are accepted",
      ],
    },
  },
  BOOKING: {
    title: "Bookings",
    description: "Accepted commercial terms, milestone payments and execution readiness.",
    tips: [
      "Booking flow: DRAFT → PENDING ACCEPTANCE → CONFIRMED → COMPLETED",
      "Milestones track the payment schedule; each is linked to a planned date",
      "Event planning and operations begin only after booking is CONFIRMED",
    ],
    pages: {
      "Bookings": [
        "Filter by status to see which bookings need submission or confirmation",
        "CONFIRMED bookings can be linked to event planning and operations",
      ],
      "Booking Detail": [
        "Submit the booking from DRAFT to PENDING_ACCEPTANCE for client review",
        "Confirm the booking after client acceptance to unlock event planning",
        "Milestones can only be updated while the booking is CONFIRMED",
      ],
    },
  },
  PLANNING: {
    title: "Event Planning",
    description: "Design events, manage planning cases and build proposals.",
    tips: [
      "Event creation requires a confirmed booking from a qualified lead",
      "Cost sheets must be approved by the Event Manager before a proposal is issued",
      "Capture all client requirements before building the cost sheet",
    ],
    pages: {
      "Event Planning": [
        "Use the Requirements panel to capture all client-specified must-haves",
        "Revision history tracks all proposal changes with documented reasons",
      ],
      "Event Design": [
        "The event brief provides the foundation for all scope and commercial decisions",
        "Blueprint approval unlocks the Scope and Requirements tabs for full detail capture",
        "Activate the event once the blueprint is approved and booking is confirmed",
      ],
      "Planning Cases": [
        "Assigned planning cases appear here for the Event Planner role",
        "Link the case to an active booking before starting the cost sheet",
      ],
    },
  },
  EXECUTION: {
    title: "Event Execution",
    description: "Manage live event operations and resolve incidents.",
    tips: [
      "Operations sessions must be opened before checkpoints can be logged",
      "Incidents require severity classification and an assigned owner",
      "Close-out exceptions must be resolved before the fiscal period can be closed",
    ],
    pages: {
      "Active Events": [
        "Events enter ACTIVE state after the planning handoff is confirmed",
        "All operational changes trigger maker-checker approval notifications",
      ],
      "Event Detail": [
        "Review the event status and team assignments before opening an operations session",
        "Scope changes during execution must be logged as formal Change Requests",
        "All stage transitions are audit-logged with actor, timestamp and reason",
      ],
      "Event Operations": [
        "Open an operations session to begin logging operational checkpoints",
        "LIVE status requires at least one open session and all readiness checks complete",
        "Document all incidents immediately — severity escalation follows the SLA matrix",
      ],
      "Event Closeout": [
        "All operational exceptions must be resolved before submitting the closeout report",
        "Post-event financials are locked after closeout submission",
        "Capture testimonial and feedback evidence before marking closeout complete",
      ],
      "Event Financials": [
        "Budget actuals are read-only here — amendments require a formal Change Request",
        "Financial close-out requires Finance Manager approval",
        "All budget variances above threshold require a documented explanation",
      ],
    },
  },
  FINANCE: {
    title: "Payments & Finance",
    description: "AR invoices, receipts, AP purchases and GL journals.",
    tips: [
      "AR: Receipt verification (Tenant Admin) and allocation (Finance Manager) are SoD-separated",
      "AP: GRN delivery confirmation must precede invoice review",
      "GL: Journals must balance (DR = CR) and pass maker-checker approval before posting",
    ],
    pages: {
      "Accounts Receivable": [
        "Invoice flow: DRAFT → ISSUED → PAID. Credit notes reduce the outstanding balance.",
        "Receipts at PENDING_VERIFICATION require Tenant Admin before Finance Manager allocation",
      ],
      "General Ledger": [
        "Journals must balance before submission (total DR = total CR)",
        "POSTED journals can only be corrected by reversal — no direct edits permitted",
      ],
      "Accounts Payable": [
        "GRN submission triggers the invoice review workflow for the Finance Manager",
        "Payment execution requires Finance Manager approval after invoice review",
      ],
    },
  },
  SETTINGS: {
    title: "Business Setup",
    description: "Configure business structure, integrations and user roles.",
    tips: [
      "Legal entity and operating unit setup must be completed before event creation",
      "COA v2 activation requires dual authorization — Business Owner and Finance Manager",
    ],
    pages: { "Business Setup": ["Complete setup groups in sequence — later steps depend on earlier configuration"] },
  },
  ADMINISTRATION: {
    title: "Administration",
    description: "Users, audit logs and enterprise configuration.",
    tips: [
      "Role capability changes are governed by the RBAC governance policy",
      "Audit logs are immutable — events cannot be deleted or altered",
    ],
    pages: {},
  },
  MARKETING: {
    title: "Demand Analytics",
    description: "Monitor public portal enquiry demand and conversion.",
    tips: ["Analytics are read-only — derived from live enquiry and lead records"],
    pages: {},
  },
  HELP: {
    title: "User Guide",
    description: "Platform documentation and operational guides.",
    tips: ["Operational SOPs (SOP-01A through SOP-01G) are available in the memory folder"],
    pages: {},
  },
  PLATFORM: { title: "Platform", description: "System page.", tips: [], pages: {} },
};

// ─── Opportunity stage labels + tips ─────────────────────────────────────────
const OPP_STAGE_LABELS = {
  PROSPECTING:        "Prospecting",
  QUALIFICATION:      "Qualification",
  NEEDS_ANALYSIS:     "Needs Analysis",
  VALUE_PROPOSITION:  "Value Proposition",
  DECISION_MAKERS:    "Decision Makers",
  PERCEPTION_ANALYSIS:"Perception Analysis",
  RFP:                "RFP",
  PROPOSAL:           "Proposal",
  NEGOTIATION:        "Negotiation",
  CLOSED_WON:         "Closed Won",
  CLOSED_LOST:        "Closed Lost",
};

const OPP_STAGE_TIPS = {
  PROSPECTING:         "Define the initial scope and confirm budget range with the client.",
  QUALIFICATION:       "Qualify the lead requirements, event type and feasibility.",
  NEEDS_ANALYSIS:      "Document detailed event requirements before building the proposal.",
  VALUE_PROPOSITION:   "Present the value proposition — align scope with client expectations.",
  DECISION_MAKERS:     "Identify and engage all decision makers and confirm approval authority.",
  PERCEPTION_ANALYSIS: "Address objections and confirm the proposal meets client expectations.",
  RFP:                 "Prepare a detailed RFP response. Confirm all technical, commercial and timeline requirements with the client.",
  PROPOSAL:            "Cost sheet must be finalized and approved before sending the proposal.",
  NEGOTIATION:         "Track all change requests; ensure revised terms are formally documented.",
  CLOSED_WON:          "Proceed to Booking once commercial terms are accepted by the client.",
  CLOSED_LOST:         "Document the reason for closure and update pipeline forecasts accordingly.",
};

const OPP_STAGE_COLORS = {
  CLOSED_WON:  { bg: "#DCFCE7", text: "#15803D", border: "rgba(21,128,61,0.3)" },
  CLOSED_LOST: { bg: "#FEE2E2", text: "#C64040", border: "rgba(198,64,64,0.3)" },
};
const OPP_STAGE_DEFAULT = { bg: "#EEF4FF", text: "#0176D3", border: "rgba(1,118,211,0.25)" };

// ─── Booking status labels + next-action tips ─────────────────────────────────
const BOOKING_STATUS_LABELS = {
  DRAFT:              "Draft",
  PENDING_ACCEPTANCE: "Pending Acceptance",
  CONFIRMED:          "Confirmed",
  COMPLETED:          "Completed",
  CANCELLED:          "Cancelled",
};

const BOOKING_NEXT_ACTION = {
  DRAFT:              "Submit the booking for client acceptance to progress to the next stage.",
  PENDING_ACCEPTANCE: "Awaiting client confirmation. Follow up if no response within the agreed timeframe.",
  CONFIRMED:          "Booking confirmed. Event planning and operations workflows can now begin.",
  COMPLETED:          "Booking complete. Initiate financial close-out and post-event feedback collection.",
  CANCELLED:          "Booking cancelled. Review the amendment register for reinstatement options.",
};

const BOOKING_STATUS_COLORS = {
  DRAFT:              { bg: "#F3F4F6", text: "#374151", border: "#D1D5DB" },
  PENDING_ACCEPTANCE: { bg: "#FEF3C7", text: "#92400E", border: "#FCD34D" },
  CONFIRMED:          { bg: "#DCFCE7", text: "#065F46", border: "#6EE7B7" },
  COMPLETED:          { bg: "#DBEAFE", text: "#1E40AF", border: "#93C5FD" },
  CANCELLED:          { bg: "#FEE2E2", text: "#991B1B", border: "#FCA5A5" },
};

// ─── Event status tips ────────────────────────────────────────────────────────
const EVENT_STATUS_ACTIONS = {
  PLANNING:  "Assign all sub-vendors and confirm operational requirements before transition to CONFIRMED.",
  CONFIRMED: "Proceed to operational briefing. All partner confirmations must be received.",
  LIVE:      "Monitor all operational checkpoints in the Operations Workspace. Log incidents immediately.",
  COMPLETED: "Submit the close-out report after resolving all operational exceptions.",
  CLOSED:    "Event is closed. Access financials and document any outstanding reconciliation items.",
  CANCELLED: "Review and document all downstream implications — financial and operational.",
};

// ─── Recommendation action icons & priority styles ────────────────────────────
const ACTION_ICONS = {
  CALL: PhoneCall, MEETING: Calendar, TASK: ClipboardList,
  FOLLOW_UP: Clock, MESSAGE: Send, NOTE: FileText, DEFAULT: Zap,
};

const PRIORITY_STYLE = {
  CRITICAL: { bg: "#FFF1F0", text: "#E7251F", border: "rgba(231,37,31,0.3)"  },
  HIGH:     { bg: "#FFFBEA", text: "#8C4B00", border: "rgba(201,100,0,0.3)"  },
  MEDIUM:   { bg: "#EEF4FF", text: "#0176D3", border: "rgba(1,118,211,0.3)"  },
  LOW:      { bg: "#F9F9F9", text: "#706E6B", border: "rgba(112,110,107,0.3)" },
};

const T = { gold: "#C9A15F", ivory: "#FBF7F2", charcoal: "#3A3330", slate: "#7A706A", divider: "rgba(201,161,95,0.18)" };

// ─── Shared micro-components ─────────────────────────────────────────────────
function PanelLoading({ testId = "copilot-panel-loading" }) {
  return (
    <div className="p-4 flex items-center justify-center py-6" data-testid={testId}>
      <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.gold }} />
    </div>
  );
}

function PanelError({ message, testId = "copilot-panel-error" }) {
  return (
    <div className="p-4 text-center" data-testid={testId}>
      <AlertCircle className="w-5 h-5 mx-auto mb-1" style={{ color: "#D97706", opacity: 0.6 }} />
      <p className="text-[11px]" style={{ color: T.slate }}>{message}</p>
    </div>
  );
}

function StatusBadge({ label, style: s = {} }) {
  return (
    <span
      className="text-[10px] font-semibold px-2 py-0.5 rounded-full border"
      style={{ background: s.bg || "#F3F4F6", color: s.text || T.slate, borderColor: s.border || "#D1D5DB" }}
    >
      {label}
    </span>
  );
}

function RecordHeader({ icon: Icon, title, subtitle, badge }) {
  return (
    <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}` }}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {Icon && (
            <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ background: `${T.gold}18` }}>
              <Icon className="w-3 h-3" style={{ color: T.gold }} />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold leading-snug truncate" style={{ color: T.charcoal }}>
              {title || "—"}
            </p>
            {subtitle && (
              <p className="text-[10px] mt-0.5" style={{ color: T.slate }}>{subtitle}</p>
            )}
          </div>
        </div>
        {badge}
      </div>
    </div>
  );
}

function WorkflowTip({ text }) {
  return (
    <div className="flex items-start gap-2 px-4 py-2.5" style={{ borderTop: `1px solid ${T.divider}` }}>
      <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: T.gold }} />
      <p className="text-[11px] leading-relaxed" style={{ color: T.charcoal }}>{text}</p>
    </div>
  );
}

function NavigateButton({ label, onClick, testId }) {
  return (
    <div className="px-4 py-3" style={{ borderTop: `1px solid ${T.divider}` }}>
      <button
        data-testid={testId}
        onClick={onClick}
        className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
        style={{ background: T.gold, color: "#fff" }}
      >
        {label}
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Lead Recommendations Panel ───────────────────────────────────────────────
function LeadRecsPanel({ leadId, navigate }) {
  const [recs, setRecs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!leadId) return;
    setLoading(true); setError(false);
    recommendationApi.getRecommendations(leadId, false)
      .then(r => setRecs((r.data?.recommendations || []).slice(0, 4)))
      .catch(() => { setError(true); setRecs([]); })
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return <PanelLoading testId="copilot-recs-loading" />;

  if (error) {
    return (
      <div data-testid="copilot-recs-error" className="p-4 text-center">
        <p className="text-[11px]" style={{ color: T.slate }}>Unable to load recommendations.</p>
        <button onClick={() => navigate(`/engage/leads/${leadId}`)} className="mt-1.5 text-xs font-medium" style={{ color: T.gold }}>
          Open Lead Detail
        </button>
      </div>
    );
  }

  if (!recs.length) {
    return (
      <div className="p-5 text-center" data-testid="copilot-recs-empty">
        <CheckCircle2 className="w-7 h-7 mx-auto mb-2" style={{ color: T.gold, opacity: 0.5 }} />
        <p className="text-xs font-medium" style={{ color: T.charcoal }}>All actions complete</p>
        <p className="text-[11px] mt-0.5" style={{ color: T.slate }}>No pending workflow actions for this stage.</p>
      </div>
    );
  }

  const primary   = recs[0];
  const secondary = recs.slice(1);
  const PrimaryIcon = ACTION_ICONS[primary.action_type] || ACTION_ICONS.DEFAULT;
  const badge = PRIORITY_STYLE[primary.priority] || PRIORITY_STYLE.MEDIUM;

  return (
    <div data-testid="copilot-lead-recs">
      {/* Primary action */}
      <div className="p-4" style={{ background: "#FFFDF9", borderBottom: `1px solid ${T.divider}` }}>
        <div className="flex items-center justify-between mb-2">
          <StatusBadge label={primary.priority} style={badge} />
          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: `${T.gold}18` }}>
            <PrimaryIcon className="w-3 h-3" style={{ color: T.gold }} />
          </div>
        </div>
        <p data-testid="copilot-primary-title"
          className="font-semibold text-sm leading-snug mb-1.5"
          style={{ color: T.charcoal, fontFamily: "'Cormorant Garamond', serif" }}>
          {primary.title}
        </p>
        <p className="text-[11px] leading-relaxed mb-2" style={{ color: T.slate }}>
          {primary.explanation}
        </p>
        {primary.suggested_timing && (
          <p className="text-[10px] mb-3" style={{ color: T.slate }}>
            <span style={{ color: T.gold }}>When: </span>{primary.suggested_timing}
          </p>
        )}
        <button
          data-testid="copilot-continue-btn"
          onClick={() => {
            const el = document.querySelector('[data-testid="next-best-action-panel"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
            else navigate(`/engage/leads/${leadId}`);
          }}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
          style={{ background: T.gold, color: "#fff" }}
        >
          Go to NBA Panel <ArrowRight className="w-3 h-3" />
        </button>
        <p className="text-[10px] text-center mt-1.5" style={{ color: T.slate }}>
          Full action routing is in the NBA panel on this page.
        </p>
      </div>
      {/* Secondary actions */}
      {secondary.length > 0 && (
        <div style={{ borderBottom: `1px solid ${T.divider}` }}>
          <p className="px-4 pt-3 pb-1 text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color: T.slate }}>
            Also consider
          </p>
          {secondary.map(rec => {
            const SecIcon = ACTION_ICONS[rec.action_type] || ACTION_ICONS.DEFAULT;
            return (
              <div key={rec.rec_id || rec.rule_id}
                data-testid={`copilot-secondary-${rec.rule_id}`}
                className="flex items-center gap-3 px-4 py-2.5"
                style={{ borderTop: `1px solid ${T.divider}` }}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${T.gold}12` }}>
                  <SecIcon className="w-2.5 h-2.5" style={{ color: T.gold }} />
                </div>
                <p className="text-[11px] flex-1" style={{ color: T.slate }}>{rec.title}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Opportunity Panel ────────────────────────────────────────────────────────
// ─── RecordOwnerRow: shows responsible person + authority from CRM-UX-03 ─────
function RecordOwnerRow({ recordType, recordId }) {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!recordId || !recordType) return;
    responsibilityApi.getResponsibility(recordType, recordId)
      .then(r => setInfo(r.data))
      .catch(() => {}); // non-blocking
  }, [recordType, recordId]);

  if (!info) return null;

  const ownerLabel = info.assigned_to_name || (info.assigned_to ? "Assigned" : "Unassigned");
  const isBO       = user?.role === "BUSINESS_OWNER";

  return (
    <div className="px-4 py-2.5" style={{ borderBottom: `1px solid ${T.divider}`, background: "#FFFDF9" }}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[9px] font-bold tracking-[0.12em] uppercase mb-0.5" style={{ color: T.slate }}>
            Responsible
          </p>
          <p className="text-[11px] font-medium" style={{ color: T.charcoal }}>
            {ownerLabel}
          </p>
        </div>
        {isBO && info.authority === "BUSINESS_OWNER" && (
          <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
            style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" }}>
            BO Authority
          </span>
        )}
        {info.authority === "OWNER" && (
          <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
            style={{ background: "#EDF7EA", color: "#2E844A", border: "1px solid #C6E5BE" }}>
            Assigned to you
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Opportunity Panel ────────────────────────────────────────────────────────
function OpportunityPanel({ oppId, navigate, customHelpMap }) {
  const [opp, setOpp]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!oppId) return;
    setLoading(true); setError(false);
    crmApi.getOpportunity(oppId)
      .then(r => setOpp(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [oppId]);

  if (loading) return <PanelLoading testId="copilot-opp-loading" />;
  if (error || !opp) return <PanelError message="Unable to load opportunity. Ensure you have access to this record." testId="copilot-opp-error" />;

  const stageColor = OPP_STAGE_COLORS[opp.stage] || OPP_STAGE_DEFAULT;
  const stageTip   = OPP_STAGE_TIPS[opp.stage];
  const stageLabel = OPP_STAGE_LABELS[opp.stage] || opp.stage;

  return (
    <div data-testid="copilot-opp-panel">
      <RecordHeader
        icon={Tag}
        title={opp.opportunity_name || opp.name || "Opportunity"}
        subtitle={opp.account_name}
        badge={<StatusBadge label={stageLabel} style={stageColor} />}
      />
      <RecordOwnerRow recordType="opportunity" recordId={oppId} />
      {stageTip && (
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}`, background: "#FFFDF9" }}>
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: T.gold }}>
            Stage Guidance
          </p>
          <p className="text-[11px] leading-relaxed" style={{ color: T.charcoal }}>{stageTip}</p>
        </div>
      )}
      {/* Workflow guide for this section */}
      {(customHelpMap?.get("page:OPPORTUNITY:Opportunity Detail") || SECTION_GUIDE.OPPORTUNITY.pages["Opportunity Detail"] || []).map((tip, i) => (
        <WorkflowTip key={i} text={tip} />
      ))}
      <NavigateButton
        testId="copilot-opp-view-btn"
        label="View Opportunity"
        onClick={() => navigate(`/crm/opportunities/${oppId}`)}
      />
    </div>
  );
}

// ─── Booking Panel ────────────────────────────────────────────────────────────
function BookingPanel({ bookingId, navigate, customHelpMap }) {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    setLoading(true); setError(false);
    bookingApi.get(bookingId)
      .then(r => setBooking(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [bookingId]);

  if (loading) return <PanelLoading testId="copilot-booking-loading" />;
  if (error || !booking) return <PanelError message="Unable to load booking. Ensure you have access to this record." testId="copilot-booking-error" />;

  const statusCfg    = BOOKING_STATUS_COLORS[booking.status] || BOOKING_STATUS_COLORS.DRAFT;
  const statusLabel  = BOOKING_STATUS_LABELS[booking.status] || booking.status;
  const nextAction   = BOOKING_NEXT_ACTION[booking.status];

  return (
    <div data-testid="copilot-booking-panel">
      <RecordHeader
        icon={CreditCard}
        title={booking.booking_number || "Booking"}
        subtitle={booking.opportunity_name || booking.client_name}
        badge={<StatusBadge label={statusLabel} style={statusCfg} />}
      />
      <RecordOwnerRow recordType="booking" recordId={bookingId} />
      {nextAction && (
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}`, background: "#FFFDF9" }}>
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: T.gold }}>
            Next Action
          </p>
          <p className="text-[11px] leading-relaxed" style={{ color: T.charcoal }}>{nextAction}</p>
        </div>
      )}
      {booking.booking_value && (
        <div className="px-4 py-2.5 flex items-center gap-2" style={{ borderTop: `1px solid ${T.divider}` }}>
          <p className="text-[10px]" style={{ color: T.slate }}>
            <span className="font-semibold" style={{ color: T.charcoal }}>
              {booking.currency || ""} {Number(booking.booking_value).toLocaleString()}
            </span>
            {" "}total booking value
          </p>
        </div>
      )}
      {(customHelpMap?.get("page:BOOKING:Booking Detail") || SECTION_GUIDE.BOOKING.pages["Booking Detail"] || []).map((tip, i) => (
        <WorkflowTip key={i} text={tip} />
      ))}
      <NavigateButton
        testId="copilot-booking-view-btn"
        label="View Booking"
        onClick={() => navigate(`/crm/bookings/${bookingId}`)}
      />
    </div>
  );
}

// ─── Event Detail Panel (CRM Events) ─────────────────────────────────────────
function EventDetailPanel({ eventId, currentPage, navigate, customHelpMap }) {
  const [ev, setEv]           = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true); setError(false);
    eventApi.getEvent(eventId)
      .then(r => setEv(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading) return <PanelLoading testId="copilot-event-loading" />;
  if (error || !ev) return <PanelError message="Unable to load event. Ensure you have access to this record." testId="copilot-event-error" />;

  const statusAction = EVENT_STATUS_ACTIONS[ev.status];
  const pageTips = customHelpMap?.get(`page:EXECUTION:${currentPage}`) || (SECTION_GUIDE.EXECUTION?.pages?.[currentPage] ?? []);

  return (
    <div data-testid="copilot-event-panel">
      <RecordHeader
        icon={CalendarDays}
        title={ev.event_name || "Event"}
        subtitle={ev.event_date ? `Date: ${new Date(ev.event_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : undefined}
        badge={
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full border"
            style={{ background: "#EEF4FF", color: "#0176D3", borderColor: "rgba(1,118,211,0.25)" }}
          >
            {ev.status}
          </span>
        }
      />
      <RecordOwnerRow recordType="event" recordId={eventId} />
      {statusAction && (
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}`, background: "#FFFDF9" }}>
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: T.gold }}>
            Current Status Guidance
          </p>
          <p className="text-[11px] leading-relaxed" style={{ color: T.charcoal }}>{statusAction}</p>
        </div>
      )}
      {pageTips.map((tip, i) => <WorkflowTip key={i} text={tip} />)}
      {currentPage !== "Event Detail" && (
        <NavigateButton
          testId="copilot-event-view-btn"
          label="Event Overview"
          onClick={() => navigate(`/crm/events/${eventId}`)}
        />
      )}
    </div>
  );
}

// ─── Section Guide Panel ──────────────────────────────────────────────────────
function SectionGuidePanel({ ctx, navSections, customHelpMap }) {
  const [workflowOpen, setWorkflowOpen] = useState(true);
  const guide          = SECTION_GUIDE[ctx.section] || SECTION_GUIDE.PLATFORM;
  const defaultPageTips = (guide.pages || {})[ctx.page] || [];
  const pageTips        = customHelpMap?.get(`page:${ctx.section}:${ctx.page}`) || defaultPageTips;
  const effectiveTips   = customHelpMap?.get(`section:${ctx.section}`) || guide.tips || [];
  const quickItems = (navSections || [])
    .filter(s => !["uat", "help", "home"].includes(s.id))
    .flatMap(s => s.items)
    .slice(0, 8);

  return (
    <div data-testid="copilot-section-guide">
      <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}` }}>
        <div className="flex items-center gap-2 mb-1">
          <Info className="w-3.5 h-3.5 flex-shrink-0" style={{ color: T.gold }} />
          <p className="text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color: T.gold }}>
            Module Overview
          </p>
        </div>
        <p className="text-[11px] leading-relaxed" style={{ color: T.slate }}>{guide.description}</p>
      </div>

      {pageTips.length > 0 && (
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}` }}>
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-3.5 h-3.5 flex-shrink-0" style={{ color: T.gold }} />
            <p className="text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color: T.gold }}>
              On this page
            </p>
          </div>
          <div className="space-y-2">
            {pageTips.map((tip, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: T.gold }} />
                <p className="text-[11px] leading-relaxed" style={{ color: T.charcoal }}>{tip}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {effectiveTips.length > 0 && (
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}` }}>
          <button
            className="w-full flex items-center justify-between"
            onClick={() => setWorkflowOpen(o => !o)}
            data-testid="copilot-workflow-toggle"
          >
            <p className="text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color: T.gold }}>
              Workflow Guide
            </p>
            {workflowOpen
              ? <ChevronUp   className="w-3 h-3" style={{ color: T.slate }} />
              : <ChevronDown className="w-3 h-3" style={{ color: T.slate }} />
            }
          </button>
          {workflowOpen && (
            <div className="mt-2 space-y-2">
              {effectiveTips.map((tip, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: T.gold }} />
                  <p className="text-[11px] leading-relaxed" style={{ color: T.charcoal }}>{tip}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {quickItems.length > 0 && (
        <div className="px-4 py-3">
          <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-2" style={{ color: T.gold }}>
            Quick Access
          </p>
          <div className="space-y-0.5">
            {quickItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`copilot-quicklink-${item.testId || item.to.replace(/\//g, "-")}`}
                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[11px] transition-colors"
                style={({ isActive }) => ({
                  color:      isActive ? T.gold   : T.slate,
                  background: isActive ? "#F0E8DA" : "transparent",
                  fontWeight: isActive ? 600 : 400,
                })}
                onMouseEnter={e => { e.currentTarget.style.background = "#F5F0EB"; }}
                onMouseLeave={e => {
                  if (!e.currentTarget.getAttribute("aria-current")) {
                    e.currentTarget.style.background = "transparent";
                  }
                }}
              >
                {item.icon && <item.icon className="w-3 h-3 flex-shrink-0" />}
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Co-Pilot Drawer (exported) ───────────────────────────────────────────────
export function CoPilotDrawer({ open, onClose, user, navSections }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const drawerRef = useRef(null);
  const prevFocusRef = useRef(null);

  const ctx   = resolveCtx(location.pathname);
  const guide = SECTION_GUIDE[ctx.section] || SECTION_GUIDE.PLATFORM;

  // ── Custom help text (CRM-MDM-01): fetch tenant overrides silently ────
  const [customHelpMap, setCustomHelpMap] = useState(new Map());
  useEffect(() => {
    if (!open) return;
    crmMdmApi.getCopilotHelp()
      .then(r => {
        const m = new Map();
        (r.data?.items || []).forEach(item => {
          const key = item.page
            ? `page:${item.section}:${item.page}`
            : `section:${item.section}`;
          if (item.tips?.length) m.set(key, item.tips);
        });
        setCustomHelpMap(m);
      })
      .catch(() => {}); // Silent failure — fall through to system defaults
  }, [open]);

  // ── Keyboard accessibility + focus management ──────────────────────────
  useEffect(() => {
    if (!open) return;

    // Store the element that had focus before the drawer opened
    prevFocusRef.current = document.activeElement;

    // Move focus into the drawer (close button)
    const raf = requestAnimationFrame(() => {
      drawerRef.current?.querySelector('[data-testid="copilot-close-btn"]')?.focus();
    });

    const handleKeyDown = (e) => {
      // Escape: close and restore focus
      if (e.key === "Escape") {
        onClose();
        return;
      }
      // Tab focus-trap — MOBILE OVERLAY ONLY (not the desktop inline panel)
      if (e.key === "Tab" && window.innerWidth < 768 && drawerRef.current) {
        const focusable = Array.from(
          drawerRef.current.querySelectorAll(
            'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
          )
        ).filter(el => !el.closest('[aria-hidden="true"]'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", handleKeyDown);
      // Restore focus to the element that was focused before the drawer opened
      const prev = prevFocusRef.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  // ── Resolve entity-specific content ───────────────────────────────────
  const renderEntityContent = () => {
    if (ctx.isLeadDetail && ctx.leadId) {
      return (
        <>
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}` }}>
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase" style={{ color: T.gold }}>
              Workflow Recommendations
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: T.slate }}>
              Deterministic actions for this lead's current stage.
            </p>
            {user?.role === "BUSINESS_OWNER" && (
              <p className="text-[10px] mt-1 px-2 py-1 rounded" style={{ background: "#FEF3C7", color: "#92400E" }}>
                Business Owner Authority — you may update, qualify or convert this lead.
              </p>
            )}
          </div>
          <LeadRecsPanel leadId={ctx.leadId} navigate={navigate} />
        </>
      );
    }
    if (ctx.isOppDetail && ctx.oppId) {
      return (
        <>
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}` }}>
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase" style={{ color: T.gold }}>
              Opportunity Record
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: T.slate }}>
              Stage guidance for this opportunity.
            </p>
            {user?.role === "BUSINESS_OWNER" && (
              <p className="text-[10px] mt-1 px-2 py-1 rounded" style={{ background: "#FEF3C7", color: "#92400E" }}>
                Business Owner Authority — you may update, reassign or progress this opportunity.
              </p>
            )}
          </div>
          <OpportunityPanel oppId={ctx.oppId} navigate={navigate} customHelpMap={customHelpMap} />
        </>
      );
    }
    if (ctx.isBookingDetail && ctx.bookingId) {
      return (
        <>
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}` }}>
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase" style={{ color: T.gold }}>
              Booking Record
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: T.slate }}>
              Status and next-action guidance for this booking.
            </p>
            {user?.role === "BUSINESS_OWNER" && (
              <p className="text-[10px] mt-1 px-2 py-1 rounded" style={{ background: "#FEF3C7", color: "#92400E" }}>
                Business Owner Authority — you may update and manage this booking. Financial confirmation requires Finance Manager approval.
              </p>
            )}
          </div>
          <BookingPanel bookingId={ctx.bookingId} navigate={navigate} customHelpMap={customHelpMap} />
        </>
      );
    }
    if (ctx.isEventDetail && ctx.eventId) {
      return (
        <>
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}` }}>
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase" style={{ color: T.gold }}>
              Event Record
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: T.slate }}>
              Operational guidance for this event.
            </p>
          </div>
          <EventDetailPanel eventId={ctx.eventId} currentPage={ctx.page} navigate={navigate} customHelpMap={customHelpMap} />
        </>
      );
    }
    // Default: deterministic section guide
    return (
      <>
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.divider}` }}>
          <p className="text-[10px] font-bold tracking-[0.14em] uppercase" style={{ color: T.gold }}>
            {guide.title}
          </p>
        </div>
        <SectionGuidePanel ctx={ctx} navSections={navSections} customHelpMap={customHelpMap} />
      </>
    );
  };

  return (
    <>
      {/* Mobile backdrop — aria-hidden so screen readers skip it */}
      <div
        className="fixed inset-0 z-40 bg-black/40 md:hidden"
        onClick={onClose}
        data-testid="copilot-mobile-backdrop"
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        ref={drawerRef}
        data-testid="copilot-drawer"
        role="complementary"
        aria-label="Prashasta Assistant workflow guide"
        className={[
          "flex flex-col overflow-hidden flex-shrink-0",
          // Mobile: fixed overlay from right
          "fixed right-0 z-50",
          "top-0 bottom-0",
          "w-[min(320px,90vw)]",
          // Desktop: relative inline panel
          "md:relative md:right-auto md:top-auto md:bottom-auto",
          "md:z-auto md:w-80",
          "md:shadow-none shadow-2xl",
        ].join(" ")}
        style={{ background: "#FAF7F2", borderLeft: "1px solid #E7E0D8" }}
      >
        {/* ── Header ── */}
        <div
          className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{ background: "#F5EFE8", borderBottom: "1px solid #E7E0D8" }}
        >
          <div className="flex items-center gap-2">
            <Compass className="w-4 h-4 flex-shrink-0" style={{ color: T.gold }} />
            <span className="text-sm font-bold"
              style={{ color: T.charcoal, fontFamily: "'Cormorant Garamond', serif", fontSize: "0.95rem" }}>
              Prashasta Assistant
            </span>
          </div>
          <button
            data-testid="copilot-close-btn"
            onClick={onClose}
            className="w-6 h-6 rounded flex items-center justify-center transition-colors"
            style={{ color: "#78716C" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#EFE9DE"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            aria-label="Close Prashasta Assistant (Escape)"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* ── Context breadcrumb ── */}
        <div
          className="px-4 py-2 flex-shrink-0 flex items-center gap-1.5"
          style={{ background: "#EDE7DD", borderBottom: "1px solid #E7E0D8" }}
          data-testid="copilot-context-banner"
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "#78716C" }}>
            {ctx.section}
          </span>
          {ctx.page && (
            <>
              <span className="text-[10px]" style={{ color: "#D6CEBF" }}>/</span>
              <span className="text-[10px] font-medium" style={{ color: "#57534E" }}>{ctx.page}</span>
            </>
          )}
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto">
          {renderEntityContent()}
        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 px-4 py-2"
          style={{ borderTop: "1px solid #E7E0D8", background: "#F5EFE8" }}>
          <p className="text-[9px] text-center" style={{ color: "#B8AFA6" }}>
            Deterministic workflow guidance — No AI
          </p>
        </div>
      </aside>
    </>
  );
}
